import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Stack,
  IconButton,
  Card,
  useTheme,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import axios from "axios";
import toast from "react-hot-toast";

import AddPlans from "src/components/whiteLable/AddPlans";
import ConfirmDialog from "src/components/whiteLable/ConfirmDialog";
import PlanShow from "src/components/whiteLable/PlanShow";
import { Seo } from "src/components/seo";
import { API_SERVICE_BACKEND } from "src/config";
import { getSessionStorage } from "src/utils/storage";
import { WhiteLabelConfigurationContext } from "src/contexts/ClientConfiguration";

const Plans = () => {
  const theme = useTheme();

  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [open, setOpen] = useState({
    open: false,
    data: null,
  });
  const [openDeleteModal, setOpenDeleteModal] = useState({
    open: false,
    data: null,
  });

  const email = getSessionStorage("userEmail");

  const getAllWhiteLabelClients = async () => {
    try {
      setIsLoading(true);
      // const response = await fetch1(
      //   `${API_SERVICE_BACKEND}/plans?userEmail=${encodeURIComponent(email)}`
      // );
      const response = await axios.get(
        `${API_SERVICE_BACKEND}/plans?userEmail=${encodeURIComponent(email)}`
      );
      const data = response.data;
      setPlans(data || []);
    } catch (error) {
      //console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllWhiteLabelClients();
  }, [email]);

  const handleEdit = (data) => {
    setOpen({ open: true, data: data });
  };

  const handleDelete = async (data) => {
    try {
      await axios.delete(`${API_SERVICE_BACKEND}/plans/${data._id}`);

      getAllWhiteLabelClients();
      toast.success("Plan deleted successfully!");
    } catch (error) {
      toast.error(
        error.response?.data?.error || error.message || "Failed to delete plan"
      );
    } finally {
      setOpenDeleteModal({ open: false, data: null });
    }
  };

  const { metaTitle, companyName } = React.useContext(
    WhiteLabelConfigurationContext
  );
  const pageTitle = `Partner/White Label: Plans - ${metaTitle || companyName}`;

  return (
    <>
      <Seo title={pageTitle} />
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            Plans
          </Typography>
          <Button
            variant="contained"
            onClick={() => setOpen({ open: true, data: null })}
            sx={{
              marginLeft: "auto",
              color: "#ffffff",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Add New Plan
          </Button>
        </Box>

        {/* Loader */}
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "300px",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : plans.length > 0 ? (
          <Grid
            container
            spacing="8px"
            sx={{
              paddingRight: "20px",
              paddingBottom: "25px",
              color: theme.palette.text.primary,
            }}
            style={{ marginTop: "0px" }}
          >
            {plans.map((plan, index) => (
              <Grid
                item
                key={index}
                xs={12}
                sm={6}
                md={4}
                sx={{ marginTop: "10px", marginBottom: "10px" }}
              >
                <Card
                  className="sodhi glass-wrapper"
                  style={{ height: "100%",borderRadius:"8px" }}
                >
                  <Stack
                    spacing={2}
                    style={{ padding: "20px 20px", height: "100%" }}
                  >
                    {/* Plan Name */}
                    <Box
                      sx={{
                        display: "flex",
                        mb: 2,
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontSize: "18px",
                          color: theme.palette.text.primary,
                        }}
                      >
                        {plan?.planName}
                      </Typography>
                      <Box>
                        <IconButton
                          onClick={() => handleEdit(plan)}
                          aria-label="edit"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          onClick={() =>
                            setOpenDeleteModal({ open: true, data: plan })
                          }
                          aria-label="delete"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>

                    <Divider
                      sx={{ borderColor: "#ECE9F5", borderWidth: "1px" }}
                    />

                    {/* Plan Features */}
                    <Stack spacing={2}>
                      <PlanShow value={plan?.monthlyCredits?.AI} label="AI" />
                      <PlanShow
                        value={plan?.monthlyCredits?.LEADS}
                        label="Leads"
                      />
                      <PlanShow
                        value={plan?.monthlyCredits?.EMAIL_VALIDATION}
                        label="Email Verifications"
                      />
                      <PlanShow
                        value={plan?.features?.LEADS_IMPORT || 0}
                        label="Leads Import"
                      />
                      <PlanShow
                        value={plan?.features?.AI_KNOWLEDGE_BASE_PAGES || 0}
                        label="AI Knowledge Base Pages"
                      />
                      <PlanShow
                        value={plan?.features?.MULTICHANNEL_ACCOUNTS}
                        label={`Multichannel Account${
                          plan?.features?.MULTICHANNEL_ACCOUNTS > 1 ? "s" : ""
                        }`}
                      />
                      <PlanShow
                        value={plan?.features?.TEAM_MEMBERS}
                        label={`Team Member${
                          plan?.features?.TEAM_MEMBERS > 1 ? "s" : ""
                        }`}
                      />
                      <PlanShow
                        value={plan?.features?.DFY_DOMAINS}
                        label={`DFY Domain${
                          plan?.features?.DFY_DOMAINS > 1 ? "s" : ""
                        }`}
                      />
                      <PlanShow
                        value={plan?.features?.USER_MAILBOXES}
                        label={`User Brought Mailbox${
                          plan?.features?.USER_MAILBOXES > 1 ? "es" : ""
                        }`}
                      />
                      <PlanShow
                        value={plan?.features?.INTENT_DATA_TOPICS}
                        label={`Intent Topic${
                          plan?.features?.INTENT_DATA_TOPICS > 1 ? "s" : ""
                        }`}
                      />
                      <PlanShow
                        value={plan?.features?.UNIFIED_INBOX}
                        label="Unified Inbox"
                      />
                      <PlanShow
                        value={plan?.features?.DEAL_PIPELINES === 1}
                        label="Deal Pipeline"
                      />
                      <PlanShow
                        value={
                          plan?.features?.ZAPIER &&
                          plan?.features?.HUBSPOT &&
                          plan?.features?.ZOHO &&
                          plan?.features?.SALESFORCE
                        }
                        label="Integrations"
                      />
                      <PlanShow
                        value={plan?.features?.ADVANCED_FILTERS}
                        label="Advanced Filters"
                      />
                      <PlanShow
                        value={plan?.features?.AI_AUTO_REPLIES}
                        label="AI Auto Replies"
                      />
                    </Stack>
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "300px",
              flexDirection: "column",
              color: theme.palette.text.secondary,
            }}
          >
            <Typography variant="h6">No plans found</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Click "Add New Plan" to create your first plan.
            </Typography>
          </Box>
        )}

        {/* Add / Delete Dialogs */}
        <AddPlans
          getAllWhiteLabelClients={getAllWhiteLabelClients}
          setOpen={setOpen}
          open={open}
        />
        <ConfirmDialog
          handleDelete={handleDelete}
          name={`Are you sure you want to delete the plan "${openDeleteModal?.data?.planName}"?`}
          setOpen={setOpenDeleteModal}
          open={openDeleteModal}
        />
      </Box>
    </>
  );
};

export default Plans;
